<template>
  <div>
    <!-- Loading indicator -->
    <div v-if="loadingCustomers" class="mx-6 mb-4">
      <v-alert type="info" dense text class="mb-0">
        <v-progress-circular indeterminate size="16" class="mr-2"></v-progress-circular>
        Loading customer details...
      </v-alert>
    </div>

    <!-- Primary Case Details -->
    <div class="text-sm my-0 p-6 bg-white dark-bg-custom">
      <v-row v-for="(row, rowIndex) in organizedData" :key="rowIndex">
        <v-col
          v-for="(item, colIndex) in row"
          :key="colIndex"
          class="px-4 py-1 column-container"
          cols="4"
        >
          <template v-if="item">
            <div class="field-container">
              <strong class="text-base flex-shrink-0">{{ item.label }} :</strong>
              <div v-if="item.isCrossLink" class="text-base ml-2 flex flex-wrap gap-1 flex-1 min-w-0">
                <span
                  v-for="(linkCase, idx) in item.linkedCasesList"
                  :key="idx"
                  class="cursor-pointer text-blue-600 font-semibold hover:text-blue-800 hover:underline transition-all duration-200"
                  @click="navigateToLoanManagement(linkCase)"
                >
                  {{ linkCase.caseNo }}{{ idx < item.linkedCasesList.length - 1 ? ',' : '' }}
                </span>
              </div>
              <div
                v-else
                class="text-base ml-2 field-value"
                :class="{
                  'cursor-pointer text-blue-600 font-semibold hover:text-blue-800 hover:underline transition-all duration-200':
                    item?.clickEvent
                }"
                @click="item?.clickEvent ? routeToCustomerByCase(item) : null"
                :title="item.value"
              >
                {{ item.value }}
              </div>
            </div>
          </template>
        </v-col>
      </v-row>
    </div>

    <!-- Co-Applicant Details Sections -->
    <div v-if="isLoanCase && borrowerTabs.length > 1" class="mx-6">
      <div v-for="(borrower, index) in borrowerTabs" :key="borrower.id" class="mb-4">
        <div class="bg-blue-50 p-3 rounded-t-md border-l-4 border-blue-500">
          <h5 class="text-base font-semibold text-blue-800 flex items-center">
            <v-icon class="mr-2" color="blue">
              {{ index === 0 ? 'mdi-account-star' : 'mdi-account-plus' }}
            </v-icon>
            {{ borrower.name }}
          </h5>
        </div>

        <div class="text-sm p-6 bg-white rounded-b-md border border-t-0">
          <v-row v-for="(row, rowIndex) in getBorrowerOrganizedData(borrower)" :key="rowIndex">
            <v-col
              v-for="(item, colIndex) in row"
              :key="colIndex"
              class="px-4 py-1 column-container"
              cols="4"
            >
              <template v-if="item">
                <div class="field-container">
                  <strong class="text-base flex-shrink-0">{{ item.label }} :</strong>
                  <div v-if="item.isCrossLink" class="text-base ml-2 flex flex-wrap gap-1 flex-1 min-w-0">
                    <span
                      v-for="(linkCase, idx) in item.linkedCasesList"
                      :key="idx"
                      class="cursor-pointer text-blue-600 font-semibold hover:text-blue-800 hover:underline transition-all duration-200"
                      @click="navigateToLoanManagement(linkCase)"
                    >
                      {{ linkCase.caseNo }}{{ idx < item.linkedCasesList.length - 1 ? ',' : '' }}
                    </span>
                  </div>
                  <div
                    v-else
                    class="text-base ml-2 field-value"
                    :class="{
                      'cursor-pointer text-blue-600 font-semibold hover:text-blue-800 hover:underline transition-all duration-200':
                        item?.clickEvent
                    }"
                    @click="item?.clickEvent ? routeToCustomerByCase(item) : null"
                    :title="item.value"
                  >
                    {{ item.value }}
                  </div>
                </div>
              </template>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFullAddress, convertToTitleCase } from '@/utils/common'
export default {
  name: 'OptimizedCaseDetails',
  props: {
    selectedCase: {
      type: Object,
      required: true
    },
    configLang: {
      type: Object,
      default: () => ({})
    },
    caseConfig: {
      type: Object,
      default: () => ({})
    },
    additionalColums: {
      type: Array,
      default: () => []
    },
    isLoanCase: {
      type: Boolean,
      default: false
    },
    borrowerInfo: {
      type: Object,
      default: null
    },
    borrowerTabs: {
      type: Array,
      default: () => []
    },
    customerDetails: {
      type: Object,
      default: () => ({})
    },
    loadingCustomers: {
      type: Boolean,
      default: false
    },
    branchName: {
      type: String,
      default: '-'
    }
  },

  mounted() {
    this.refreshBorrowerData()
  },

  watch: {
    selectedCase: {
      handler() {
        this.refreshBorrowerData()
      },
      deep: true
    },
    isLoanCase() {
      if (this.isLoanCase) {
        this.refreshBorrowerData()
      }
    }
  },

  computed: {
    organizedData() {
      const allFields = this.getAllFields
      return this.organizeIntoRows(allFields)
    },
    getAllFields() {
      const fields = []

      if (this.selectedCase?.caseNo) {
        fields.push({
          label: this.configLang.caseNo?.displayName || 'Case No',
          value: this.selectedCase.caseNo
        })
      }

      if (this.selectedCase?.userDefinedCaseStatus) {
        fields.push({
          label: this.configLang.userDefinedCaseStatus?.displayName || 'Outcome',
          value: this.selectedCase.userDefinedCaseStatus
        })
      }

      if (this.selectedCase?.branch) {
        fields.push({
          label: 'Branch',
          value: this.branchName
        })
      }

      // Primary Customer information (only show in main details, not in borrower sections)
      if (this.selectedCase.customer?.customerFullName) {
        const customerLabel =
          this.caseConfig.tableTabMapping?.customerTable?.displayName || 'Primary Customer'
        fields.push({
          label: `${customerLabel} Name`,
          value: this.selectedCase.customer.customerFullName,
          clickEvent: true,
          customer: this.selectedCase.customer
        })
      }

      if (this.selectedCase.customer?.customerAddress?.length) {
        const customerLabel =
          this.caseConfig.tableTabMapping?.customerTable?.displayName || 'Primary Customer'
        fields.push({
          label: `${customerLabel} Address`,
          value: this.customerAddress(this.selectedCase.customer.customerAddress[0]) || '-'
        })
      }

      if (this.isLoanCase) {
        const loanFields = this.getLoanFields()
        fields.push(...loanFields)

        // Add co-applicant count summary
        if (this.selectedCase?.loanDetails?.coApplicants?.length) {
          fields.push({
            label: 'Total Co-Applicants',
            value: this.selectedCase.loanDetails.coApplicants.length
          })
        }

        // Add guarantor information in organized data
        this.addGuarantorFields(fields)
      }

      // Case dates
      if (this.selectedCase.caseStartDate) {
        const caseLabel = this.caseConfig.tableTabMapping?.caseTable?.displayName || 'Case'
        fields.push({
          label: `${caseLabel} Start Date`,
          value: this.$dayjs(this.selectedCase.caseStartDate).format('DD/MM/YYYY hh:mm A')
        })
      }

      if (this.selectedCase.caseEndDate) {
        const caseLabel = this.caseConfig.tableTabMapping?.caseTable?.displayName || 'Case'
        fields.push({
          label: `${caseLabel} End Date`,
          value: this.$dayjs(this.selectedCase.caseEndDate).format('DD/MM/YYYY hh:mm A')
        })
      }

      if (this.selectedCase.taskStartDateTime) {
        fields.push({
          label: this.configLang.taskStartDateTime?.displayName || 'Task Start Date',
          value: this.$dayjs(this.selectedCase.taskStartDateTime).format('DD/MM/YYYY hh:mm A')
        })
      }

      if (this.selectedCase.taskEndDateTime) {
        fields.push({
          label: this.configLang.taskEndDateTime?.displayName || 'Task End Date',
          value: this.$dayjs(this.selectedCase.taskEndDateTime).format('DD/MM/YYYY hh:mm A')
        })
      }

      const additionalFields = this.getAdditionalFields()
      fields.push(...additionalFields)

      if (this.selectedCase?.productInfo) {
        fields.push({
          label: this.configLang.productInfo?.displayName || 'Product Info',
          value: this.selectedCase.productInfo
        })
      }
      return fields
    }
  },

  methods: {
    getLoanFields() {
      const loanFields = []
      const loanDetails = this.selectedCase.loanDetails

      if (!loanDetails) return loanFields

      const loanFieldsConfig = [
        { key: 'typeOfLoan', label: this.configLang.typeOfLoan?.displayName || 'Type of Loan' },
        { key: 'loanCategory', label: 'Loan Segment' },
        {
          key: 'loanAmountRequested',
          label: this.configLang.loanAmountRequested?.displayName || 'Loan Amount',
          format: 'currency'
        },
        {
          key: 'loanAmountSanctioned',
          label: this.configLang.loanAmountSanctioned?.displayName || 'Sanctioned Amount',
          format: 'currency'
        },
        {
          key: 'tenureMonths',
          fallbackKey: 'tenure',
          label: this.configLang.tenure?.displayName || 'Tenure',
          suffix: ' months'
        },
        {
          key: 'annualProcessingRate',
          label: this.configLang.annualProcessingRate?.displayName || 'Annual Processing Fee',
          format: 'interest'
        },
        {
          key: 'interestRate',
          label: this.configLang.interestRate?.displayName || 'Interest Rate',
          format: 'interest'
        },
        {
          key: 'processingFee',
          label: this.configLang.processingFee?.displayName || 'Processing Fee',
          format: 'processingFee'
        },
        {
          key: 'currentStatus',
          label: this.configLang.currentStatus?.displayName || 'Current Status'
        },
        { key: 'kycStatus', label: this.configLang.kycStatus?.displayName || 'KYC Status' },
        {
          key: 'loanWorkflow',
          label: 'Workflow Template',
          format: 'nested',
          nestedKey: 'name'
        },
        {
          key: 'principalOutstanding',
          label: 'Principal Outstanding',
          format: 'currency'
        },
        {
          key: 'totalAmountDisbursed',
          label: 'Total Disbursed',
          format: 'currency'
        },
        {
          key: 'emiAmount',
          label: 'EMI Amount',
          format: 'currency'
        },
        {
          key: 'totalEmiPaid',
          label: 'Total EMI Paid'
        },
        {
          key: 'overdueAmount',
          label: 'Overdue Amount',
          format: 'currency'
        },
        {
          key: 'latePaymentFee',
          label: 'Late Payment Fee',
          format: 'latePaymentFee'
        },
        {
          key: 'prepaymentCharges',
          label: 'Prepayment Charges',
          suffix: '%'
        },
        {
          key: 'collateralType',
          label: 'Collateral Type'
        },
        {
          key: 'cibilScore',
          label: 'CIBIL Score'
        },
        {
          key: 'panNumber',
          label: 'PAN Number'
        },
        {
          key: 'aadhaarNumber',
          label: 'Aadhaar Number'
        },
        {
          key: 'disbursementDate',
          label: 'Disbursement Date',
          format: 'date'
        },
        {
          key: 'sanctionDate',
          label: 'Sanction Date',
          format: 'date'
        },
        {
          key: 'closureDate',
          label: 'Closure Date',
          format: 'date'
        },
        {
          key: 'emiStartDate',
          label: 'EMI Start Date',
          format: 'date'
        },
        {
          key: 'emiEndDate',
          label: 'EMI End Date',
          format: 'date'
        },
        {
          key: 'nextEmiDate',
          label: 'Next EMI Date',
          format: 'date'
        }
      ]

      loanFieldsConfig.forEach((config) => {
        let value = null
        if (config.format === 'nested') {
          value = loanDetails[config.key]?.[config.nestedKey]
        } else {
          value =
            loanDetails[config.key] !== undefined && loanDetails[config.key] !== null
              ? loanDetails[config.key]
              : loanDetails[config.fallbackKey]
        }

        if (value !== undefined && value !== null && value !== '') {
          if (config.format === 'currency') {
            value = `₹ ${this.formatCurrency(value)}`
          } else if (config.format === 'interest') {
            value =
              config.key === 'annualProcessingRate'
                ? `${value.toFixed(2)}%`
                : `${value.toFixed(2)}% (${loanDetails.typeOfInterest || ''})`
          } else if (config.format === 'processingFee') {
            const totalShare = Array.isArray(value)
              ? value.reduce((sum, item) => sum + (item.share || 0), 0)
              : 0
            value = `${totalShare?.toFixed(2)}%`
          } else if (config.format === 'latePaymentFee') {
            value = `${value}% (${loanDetails.lateFeeType || ''})`
          } else if (config.format === 'date') {
            value = this.$dayjs(value).format('DD/MM/YYYY')
          } else if (config.suffix) {
            value = `${value}${config.suffix}`
          }

          loanFields.push({
            label: config.label,
            value: value
          })
        }
      })

      return loanFields
    },
    getAdditionalFields() {
      const additionalFields = []

      // Add dynamic fields from additionalColumns
      if (this.additionalColums && this.additionalColums.length) {
        this.additionalColums.forEach((column) => {
          if (column.value) {
            let displayValue = column.value
            let isCrossLink = false
            let linkedCasesList = []

            const populatedData = this.selectedCase[column.key]
            const source =
              Array.isArray(populatedData) && populatedData.length === (Array.isArray(displayValue) ? displayValue.length : 1)
                ? populatedData
                : (Array.isArray(displayValue) ? displayValue : [displayValue])

            isCrossLink = source.some(v => typeof v === 'object' && v !== null && (v.caseNo || v._id))

            if (isCrossLink) {
              linkedCasesList = source.filter(v => v !== null && v !== undefined).map((v) => {
                return {
                  caseNo: typeof v === 'object' && v !== null ? v.caseNo : v,
                  _id: typeof v === 'object' && v !== null ? v._id : v,
                  subCase: typeof v === 'object' && v !== null ? v.subCase : null
                }
              })
            } else if (Array.isArray(displayValue)) {
              displayValue = source
                .map((v) => (typeof v === 'object' && v !== null ? v.caseNo || v._id : v))
                .join(', ')
            }

            additionalFields.push({
              label: this.configLang[column.key]?.displayName || this.convertToTitle(column.key),
              value: displayValue,
              isCrossLink,
              linkedCasesList
            })
          }
        })
      }

      return additionalFields
    },

    organizeIntoRows(fields) {
      const rows = []
      const columnsPerRow = 3
      const rowsNeeded = Math.ceil(fields.length / columnsPerRow)

      for (let i = 0; i < rowsNeeded; i++) {
        rows.push(Array(columnsPerRow).fill(null))
      }

      for (let col = 0; col < columnsPerRow; col++) {
        for (let row = 0; row < rowsNeeded; row++) {
          const fieldIndex = col * rowsNeeded + row
          if (fieldIndex < fields.length) {
            rows[row][col] = fields[fieldIndex]
          }
        }
      }

      return rows
    },

    customerAddress(address) {
      return getFullAddress(address)
    },

    formatCurrency(amount) {
      return `${amount?.toLocaleString('en-IN')}`
    },
    convertToTitle(text) {
      return convertToTitleCase(text)
    },

    getBorrowerOrganizedData(borrower) {
      const fields = this.getBorrowerFields(borrower)
      return this.organizeIntoRows(fields)
    },
    getBorrowerFields(borrower) {
      const fields = []
      const customerData = borrower.customerData

      if (customerData?.customerFullName) {
        fields.push({
          label: 'Full Name',
          value: customerData.customerFullName,
          clickEvent: true,
          customer: customerData
        })
      }

      if (customerData?.customerMobile?.length) {
        fields.push({
          label: 'Mobile',
          value: Array.isArray(customerData.customerMobile)
            ? customerData.customerMobile[0]
            : customerData.customerMobile
        })
      }

      const address = Array.isArray(customerData.customerAddress)
        ? customerData.customerAddress[0]
        : customerData.customerAddress
      if (address) {
        fields.push({
          label: 'Address',
          value: this.customerAddress(address) || '-'
        })
      }

      if (borrower.share !== undefined && borrower.share > 0) {
        fields.push({
          label: 'Share Percentage',
          value: `${borrower.share}%`
        })
      }

      if (borrower.emiAmount > 0) {
        fields.push({
          label: 'EMI Amount',
          value: `₹${this.formatCurrency(borrower.emiAmount)}`
        })
      }

      return fields
    },
    addGuarantorFields(fields) {
      if (!this.selectedCase?.loanDetails?.guarantors?.length) return

      this.selectedCase.loanDetails.guarantors.forEach((guarantor, index) => {
        const customerId = guarantor.customer || guarantor._id
        const name =
          guarantor.customerFullName ||
          this.customerDetails[customerId]?.customerFullName ||
          'Unknown Guarantor'

        fields.push({
          label: `Guarantor ${index + 1}`,
          value: name,
          clickEvent: true,
          customer: guarantor.customer ? this.customerDetails[customerId] : guarantor
        })
      })
    },
    refreshBorrowerData() {
      // Emit event to parent component to refresh borrower data
      this.$emit('refresh-borrowers', this.selectedCase)
    },

    routeToCustomerByCase(item) {
      if (item.customer) {
        this.$emit('open-customer-details', item.customer)
      }
    },

    async navigateToLoanManagement(linkCase) {
      try {
        const caseId = linkCase._id;
        if (!caseId) {
          this.$toast.error('Invalid case reference');
          return;
        }
        const response = await this.$axios.get(`/collection/case/${caseId}`);
        const caseData = response.data && (response.data.case || response.data.data);
        if (!caseData) {
          this.$toast.error('Case not found');
          return;
        }

        let subCaseId = '';
        let useCase = caseData.useCase || '';

        if (caseData.subCase) {
          if (typeof caseData.subCase === 'object') {
            subCaseId = caseData.subCase._id;
            if (caseData.subCase.useCase) {
              useCase = caseData.subCase.useCase;
            }
          } else {
            subCaseId = caseData.subCase;
          }
        }

        let subCases = this.$store.state.subCases || [];
        if (!subCases.length) {
          try {
            const res = await this.$axios.get('/orgSetup/extend-usecase');
            subCases = res.data?.data || [];
            this.$store.dispatch('setSubCases', subCases);
          } catch (e) {
            console.error('Error pre-fetching subCases:', e);
          }
        }

        // Fallback: If useCase is resolved but subCaseId is not, find the first subcase of this useCase
        if (useCase && !subCaseId) {
          const matched = subCases.find(sc => sc.useCase === useCase);
          if (matched) {
            subCaseId = matched._id;
          }
        }

        // Fallback: If subCaseId is resolved but useCase is not, find useCase from subCases list
        if (subCaseId && !useCase) {
          const matched = subCases.find(sc => sc._id === subCaseId);
          if (matched) {
            useCase = matched.useCase;
          }
        }

        if (!useCase || !subCaseId) {
          this.$toast.error('Unable to resolve workspace for this case');
          return;
        }

        this.$emit('close-dialog');
        
        this.$router.push({
          path: `/workforce/${useCase}/${subCaseId}`,
          query: { caseNo: caseData.caseNo }
        });
      } catch (error) {
        console.error('Error in navigateToLoanManagement:', error);
        this.$toast.error('Error opening linked case');
      }
    },
  }
}
</script>

<style scoped>
.field-container {
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
}

.field-value {
  flex: 1;
  min-width: 0;
  word-break: break-all;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.column-container {
  position: relative;
  min-width: 0;
}

.column-container:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 1px;
  background-color: #bab7b7;
}
</style>
